import { MouseEventHandler, useCallback } from 'react';
import { Box, Card, Stack } from '@mui/material';

import { PriceGroup } from '@ocx-app/modules/stores/types';
import { useRudderStack } from '@ocx-app/lib/rudderStack/useRudderStack';
import { useFormatDistance } from '@ocx-app/lib/formatters/formatDistance';

import { FuelPriceState } from '@ocx/graphql';
import { Typography, SizingMultiplier, IconButton } from '@ocx/ui';
import { MapMarkerIcon, LocationArrowIcon } from '@ocx/ui-icons';

import { PricesGridController } from '../prices-grid/prices-grid-controller.component';

export type StoreCardProps = {
  storeId: string;
  addressLine1: string;
  addressLine2: string;
  distance: number | null;
  directionLink: string | null;
  onCardClick?: (storeId: string) => void;
  isExpanded?: boolean;
  fuelPriceState: FuelPriceState | null;
  priceGroups: PriceGroup[];
  pricesGridDefaultTitle?: string;
};

export const StoreCard = ({
  addressLine1,
  addressLine2,
  distance,
  fuelPriceState,
  priceGroups = [],
  pricesGridDefaultTitle,
  onCardClick,
  directionLink,
  isExpanded,
  storeId,
}: StoreCardProps) => {
  const { triggerEvent } = useRudderStack();
  const handleCardClick = useCallback(() => onCardClick?.(storeId), [onCardClick, storeId]);
  const formatDistance = useFormatDistance();

  const handleNavigationClick: MouseEventHandler<HTMLButtonElement> = useCallback(
    (e) => {
      e.stopPropagation();
      triggerEvent('store_locator_directions');
    },
    [triggerEvent],
  );

  return (
    <Card elevation={0} onClick={handleCardClick} sx={{ display: 'flex' }}>
      <Stack spacing={SizingMultiplier.md} p={SizingMultiplier.md} flex={1}>
        <Stack direction="row" alignItems="center" spacing={1}>
          <MapMarkerIcon sx={{ color: 'text.secondary' }} />
          <Box flexGrow="1">
            <Typography>{addressLine1}</Typography>
            <Typography variant="caption" color="textSecondary">
              {addressLine2}
            </Typography>
            {distance !== null && (
              <Typography variant="body2" color="primary">
                {formatDistance(distance)}mi
              </Typography>
            )}
          </Box>
          {directionLink && (
            <IconButton href={directionLink} onClick={handleNavigationClick} rel="noreferrer" target="_blank">
              <LocationArrowIcon />
            </IconButton>
          )}
        </Stack>
        {priceGroups.length !== 0 && (
          <Stack overflow="scroll">
            <PricesGridController
              priceGroups={priceGroups}
              isExpandedByDefault={isExpanded}
              pricesGridDefaultTitle={pricesGridDefaultTitle}
              fuelPriceState={fuelPriceState}
            />
          </Stack>
        )}
      </Stack>
    </Card>
  );
};
